// Multi-select dropdown component for filter selection

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOption = (value: string) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];

    onChange(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  const handleClearAll = () => {
    onChange([]);
  }

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    } else if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    } else if (selectedValues.length === options.length) {
      return "All selected";
    } else {
      return `${selectedValues.length} selected`;
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'hover:border-gray-400'}
          ${selectedValues.length > 0 ? 'text-gray-900' : 'text-gray-500'}
        `}
      >
        <div className="flex items-center justify-between">
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Select All Option */}
          {options.length > 1 && (
            <div className='grid grid-cols-3 w-full border-b border-gray-200'>
              <div
                onClick={handleSelectAll}
                className="col-span-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex items-center">
                  <div className={`
                    w-4 h-4 border-2 rounded mr-3 flex items-center justify-center
                    ${selectedValues.length === options.length
                      ? 'bg-blue-600 border-blue-600'
                      : selectedValues.length > 0
                        ? 'bg-blue-100 border-blue-600'
                        : 'border-gray-300'
                    }
                  `}>
                    {selectedValues.length === options.length && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                    {selectedValues.length > 0 && selectedValues.length < options.length && (
                      <div className="w-2 h-2 bg-blue-600 rounded-sm" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
                  </span>
                </div>
              </div>

              {selectedValues.length > 0 && selectedValues.length < options.length && (
                <div onClick={handleClearAll} className="col-span-auto px-3 py-2 hover:bg-gray-100 cursor-pointer">
                  <span className="text-sm text-right font-medium text-red-700">
                    Clear
                  </span>
                </div>
              )}

            </div>
          )}

          {/* Individual Options */}
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleToggleOption(option.value)}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`
                  w-4 h-4 border-2 rounded mr-3 flex items-center justify-center
                  ${selectedValues.includes(option.value)
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-gray-300'
                  }
                `}>
                  {selectedValues.includes(option.value) && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-sm text-gray-700 truncate">
                  {option.label}
                </span>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};
